import { Global } from '@emotion/react'
import React, { useEffect, useRef } from 'react'

import { Box, Color, DisplayPosition, Modal, ZIndex } from '@gousto-internal/citrus-react'

import type { FlexQueryInput } from '@library/api-menu-experience'
import { DeviceType, useDeviceType } from '@library/dom'

import { useFlexPortions } from '../../hooks/useFlexPortions'
import { FlexPortionEditor, type FlexPortionEditorProps } from './FlexPortionEditor'

export type FlexPortionEditorBoundaryProps = FlexQueryInput &
  Omit<FlexPortionEditorProps, 'numPortions' | 'state'> & {
    isOpen: boolean
    variant?: 'inline' | 'floating-tile'
  }

const FLOATING_TILE_MODAL_PORTAL_CLASS = 'flex-portion-editor-modal-portal'
const stopPropagation = (event: React.SyntheticEvent) => event.stopPropagation()

const drawerModalStyle = {
  overlay: {
    alignItems: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    zIndex: 1000,
  },
  content: {
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
    bottom: 0,
    inset: 'auto 0 0 0',
    left: 0,
    padding: 0,
    position: 'absolute',
    right: 0,
    top: 'auto',
    width: '100%',
  },
} as const

type FloatingTileShellProps = {
  children: React.ReactNode
  onClose: () => void
}

const FloatingTileShell = ({ children, onClose }: FloatingTileShellProps) => {
  const isMobile = useDeviceType() === DeviceType.MOBILE
  const editorRef = useRef<HTMLDivElement>(null)

  const handleRequestClose = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation()
    onClose()
  }

  useEffect(() => {
    if (isMobile) {
      return undefined
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (editorRef.current && !editorRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', closeOnOutsideClick)

    return () => document.removeEventListener('mousedown', closeOnOutsideClick)
  }, [isMobile, onClose])

  if (isMobile) {
    const appElement =
      typeof document === 'undefined'
        ? undefined
        : document.getElementById('react-root') ?? undefined

    return (
      <>
        <Global
          styles={{
            [`.${FLOATING_TILE_MODAL_PORTAL_CLASS} .ReactModal__Content > div`]: { width: '100%' },
          }}
        />
        <Modal
          {...(appElement ? { appElement } : { ariaHideApp: false })}
          contentLabel="Edit recipe portions"
          isOpen
          name="flex-portion-editor-modal"
          onRequestClose={handleRequestClose}
          portalClassName={FLOATING_TILE_MODAL_PORTAL_CLASS}
          shouldCloseOnEsc
          shouldCloseOnOverlayClick
          style={drawerModalStyle}
        >
          <Box
            bg={Color.White}
            data-testid="floating-flex-portion-editor"
            maxHeight="100dvh"
            onClick={stopPropagation}
            onKeyDown={stopPropagation}
            onKeyPress={stopPropagation}
            onKeyUp={stopPropagation}
            style={{ overflowY: 'auto' }}
            width="100vw"
          >
            {children}
          </Box>
        </Modal>
      </>
    )
  }

  return (
    <Box
      bottom="0"
      left="0"
      onClick={stopPropagation}
      onKeyDown={stopPropagation}
      onKeyPress={stopPropagation}
      onKeyUp={stopPropagation}
      position={DisplayPosition.Absolute}
      right="0"
      width="100%"
    >
      <Box ref={editorRef} position={DisplayPosition.Relative}>
        <Box
          bg={Color.White}
          boxShadow="0 18px 14px 0 rgba(51, 61, 71, 0.2)"
          data-testid="floating-flex-portion-editor"
          position={DisplayPosition.Absolute}
          top="1px"
          width="100%"
          zIndex={ZIndex.Mask}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export const FlexPortionEditorBoundary = (props: FlexPortionEditorBoundaryProps) => {
  const {
    isOpen,
    numPortions,
    recipeCount,
    onClose,
    onRemoveRecipe,
    onSelectPortions,
    onUpdatePortions,
    variant = 'inline',
  } = props
  const state = useFlexPortions(props)

  if (!isOpen) {
    return null
  }

  const handleUpdatePortions = (updatedNumPortions: number) => {
    onUpdatePortions(updatedNumPortions)

    if (variant === 'floating-tile') {
      onClose()
    }
  }

  const editor = (
    <FlexPortionEditor
      numPortions={numPortions}
      onClose={onClose}
      onRemoveRecipe={onRemoveRecipe}
      onSelectPortions={onSelectPortions}
      onUpdatePortions={handleUpdatePortions}
      recipeCount={recipeCount}
      state={state}
    />
  )

  return variant === 'floating-tile' ? (
    <FloatingTileShell onClose={onClose}>{editor}</FloatingTileShell>
  ) : (
    editor
  )
}
